import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../shared/auth';
import { api } from '../../shared/api';

interface OidcProbe {
  enabled: boolean;
  buttonLabel: string;
}

function readApiBase(): string {
  // mirror api.ts: same base origin, but without /api/v1 because /auth/oidc/login
  // is a full-page redirect we hit on the API host, not via the axios client.
  const raw = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
  return raw ? `${raw}/api/v1` : '/api/v1';
}

export function LoginPage() {
  const { login, acceptSsoToken } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(import.meta.env.DEV ? 'admin@census.local' : '');
  const [password, setPassword] = useState(import.meta.env.DEV ? 'admin1234' : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oidc, setOidc] = useState<OidcProbe | null>(null);

  // Probe the API once on mount: should we show the SSO button at all?
  useEffect(() => {
    let cancelled = false;
    api
      .get<OidcProbe>('/auth/oidc/config')
      .then((r) => {
        if (!cancelled) setOidc(r.data);
      })
      .catch(() => {
        if (!cancelled) setOidc({ enabled: false, buttonLabel: 'Sign in with SSO' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Catch the post-IdP redirect. The API places sso_token / sso_error in the URL
  // fragment so it never lands in server access logs or referer headers.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    const params = new URLSearchParams(hash);
    const ssoErr = params.get('sso_error');
    if (ssoErr) {
      setError(ssoErr);
      history.replaceState(null, '', window.location.pathname);
      return;
    }
    const ssoToken = params.get('sso_token');
    if (!ssoToken) return;
    const ssoExpiresAt = params.get('expiresAt') ?? undefined;
    history.replaceState(null, '', window.location.pathname);
    setBusy(true);
    acceptSsoToken(ssoToken, ssoExpiresAt)
      .then(() => navigate('/'))
      .catch((err: any) => {
        setError(err?.response?.data?.message ?? 'SSO sign-in failed');
        setBusy(false);
      });
  }, [acceptSsoToken, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  function onSsoClick() {
    // Full-page navigation — the API does a 302 to the IdP, then 302's back to
    // /login#sso_token=…, which the useEffect above catches.
    window.location.href = `${readApiBase()}/auth/oidc/login`;
  }

  return (
    <div className="grid h-full min-h-screen place-items-center bg-slate-100 p-4">
      <form
        className="card w-full max-w-sm space-y-4 p-6"
        onSubmit={onSubmit}
      >
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Census Scheduler</h1>
          <p className="text-sm text-slate-500">Sign in to continue</p>
        </div>
        <div>
          <label className="label" htmlFor="email">Email</label>
          <input id="email" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="label" htmlFor="password">Password</label>
          <input id="password" type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <div className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? 'Signing in...' : 'Sign in'}
        </button>
        {oidc?.enabled && (
          <>
            <div className="relative my-1 flex items-center">
              <div className="flex-1 border-t border-slate-200" />
              <span className="mx-3 text-xs uppercase tracking-wide text-slate-400">or</span>
              <div className="flex-1 border-t border-slate-200" />
            </div>
            <button
              type="button"
              onClick={onSsoClick}
              disabled={busy}
              className="w-full rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {oidc.buttonLabel}
            </button>
          </>
        )}
        {import.meta.env.DEV && (
          <p className="text-xs text-slate-500">
            Seeded users: <span className="font-mono">admin@census.local / admin1234</span><br />
            <span className="font-mono">provider@census.local / provider1234</span>
          </p>
        )}
      </form>
    </div>
  );
}
